<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { Bell, Check, Loader2, Mail, Megaphone, MessageCircle, Trash2, X } from '@lucide/vue'
import { computed } from 'vue'

import { useDateFormat } from '@ja/core-ui/lib/date'

import {
  resolveNotificationItemRenderer,
  type NotificationItemRenderer,
} from '../notificationItemRenderers'
import type { NotificationData } from '../types/notification'

const { formatRelative } = useDateFormat()

const props = defineProps<{
  notification: NotificationData
}>()

const emit = defineEmits<{
  markRead: [id: string]
  delete: [id: string]
}>()

const renderer = computed<NotificationItemRenderer | null>(() =>
  resolveNotificationItemRenderer(props.notification),
)

const customComponent = computed(() => renderer.value?.component ?? null)

function formatRelativeTime(dateString: string): string {
  return formatRelative(dateString)
}

const iconComponent = computed(() => {
  const icon = props.notification.icon
  if (icon === 'megaphone') return Megaphone
  if (icon === 'mail') return Mail
  if (icon === 'message-circle') return MessageCircle
  if (icon === 'check') return Check
  if (icon === 'x') return X
  return Bell
})

const priorityClass = computed(() => {
  switch (props.notification.priority) {
    case 'urgent':
      return 'border-l-destructive'
    case 'high':
      return 'border-l-warning'
    default:
      return 'border-l-transparent'
  }
})

async function handleClick() {
  if (!props.notification.is_read) {
    emit('markRead', props.notification.id)
  }

  if (renderer.value?.onClick) {
    const handled = await renderer.value.onClick(props.notification)
    if (handled) return
  }

  if (props.notification.action_url) {
    router.visit(props.notification.action_url)
  }
}

function handleDelete(event: Event) {
  event.stopPropagation()
  emit('delete', props.notification.id)
}
</script>

<template>
  <component
    :is="customComponent"
    v-if="customComponent"
    :notification="notification"
    @mark-read="(id: string) => emit('markRead', id)"
    @delete="(id: string) => emit('delete', id)"
    @click="handleClick"
  />

  <div
    v-else
    :class="[
      'group flex cursor-pointer items-start gap-3 border-l-2 px-3 py-2 transition-colors hover:bg-muted/50',
      priorityClass,
      notification.is_read ? 'opacity-60' : 'bg-muted/30',
    ]"
    @click="handleClick"
  >
    <div
      :class="[
        'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
        notification.is_read ? 'bg-muted' : 'bg-primary/10',
      ]"
    >
      <Loader2
        v-if="notification.is_progressive && notification.progress_status === 'processing'"
        class="size-4 animate-spin text-primary"
      />
      <component
        :is="iconComponent"
        v-else
        :class="['size-4', notification.is_read ? 'text-muted-foreground' : 'text-primary']"
      />
    </div>

    <div class="min-w-0 flex-1">
      <div class="flex items-start justify-between gap-2">
        <p :class="['text-sm font-medium', notification.is_read ? 'text-muted-foreground' : 'text-foreground']">
          {{ notification.title }}
        </p>
        <span class="shrink-0 text-xs text-muted-foreground">
          {{ formatRelativeTime(notification.created_at) }}
        </span>
      </div>
      <p class="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
        {{ notification.message }}
      </p>

      <div
        v-if="notification.is_progressive && notification.progress_data?.percent != null"
        class="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <div
          class="h-full bg-primary transition-all duration-300"
          :style="{ width: `${notification.progress_data.percent}%` }"
        />
      </div>
    </div>

    <button
      class="mt-0.5 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
      @click="handleDelete"
    >
      <Trash2 class="size-4" />
    </button>
  </div>
</template>
